<template>
  <ValidationProvider
    v-if="isVisible"
    v-slot="{ touched, failed, errors }"
    :name="field.errorLabel"
    :vid="field.name"
    :rules="field.rules"
    tag="div"
    class="TextField"
  >
    <p v-if="field.hasHtml" class="TextField-label" v-text="field.title"></p>
    <p v-else class="TextField-label" v-text="field.title"></p>

    <label class="TextField-inner" :for="field.name">
      <span
        class="TextField-body"
        :class="{ 'is-invalid': touched && failed, 'is-disabled': field.disabled }"
      >
        <input
          :id="field.name"
          v-model="thisValue"
          class="TextField-input"
          :class="{ 'has-value': hasValue }"
          :name="field.name"
          :title="field.title"
          :type="field.type"
          :placeholder="field.placeholder"
          :disabled="field.disabled"
          @blur="onBlur"
        />
        <i class="TextField-beforeIcon fas" :class="field.fa"></i>
        <i v-if="touched && failed" class="TextField-afterIcon fas fa-exclamation-triangle"></i>
        <span class="TextField-Placeholder" v-text="field.placeholder + requiredText"></span>
      </span>
    </label>

    <ErrorMessages :error-messages="errors" :has-error-messages="touched && errors.length > 0" />

    <p v-if="field.cautionText" class="TextField-notice t-caution" v-text="field.cautionText"></p>
  </ValidationProvider>
</template>

<script>
import FormItem from '../../mixins/FormItem'
import ErrorMessages from './ErrorMessages'

import '@fortawesome/fontawesome-free-webfonts/css/fontawesome.css'
import '@fortawesome/fontawesome-free-webfonts/css/fa-solid.css'

export default {
  name: 'TextField',
  components: {
    ErrorMessages,
  },
  mixins: [FormItem],
  methods: {
    onBlur(e) {
      if (this.field.onBlur)
        this.onChangeValue({ name: this.field.name, value: this.field.onBlur(e.target.value) })
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../css/foundation/vars';

.TextField {
  max-width: 1018px;
  margin-top: 20px;

  &:first-child {
    margin-top: 0;
  }

  &::v-deep .ErrorMessages {
    margin-top: 4px;
  }

  .PlanSelector + & {
    margin-top: 4.8rem;
  }
}

.TextField-inner {
  display: block;
  margin-top: 4px;
}

.TextField-label {
  display: block;
  width: 100%;
  padding-left: 0.5rem;
  font-size: 1.4rem;
  font-weight: bold;
}

.TextField-beforeIcon {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 1.5rem;
  display: block;
  width: 2.1rem;
  height: 2.1rem;
  margin: auto;
  font-size: 2.1rem;
  line-height: 2.1rem;
  color: vars.$color-text-changed;
  text-align: center;
  pointer-events: none;
  transition: color 0.15s ease-out, transform 0.15s ease-out;

  &.fa-user {
    width: 2rem;
    height: 2rem;
    font-size: 2rem;
    line-height: 2rem;
  }
}

.TextField-afterIcon {
  position: absolute;
  top: 0;
  right: 2.1rem;
  bottom: 0;
  display: block;
  width: 3rem;
  height: 3rem;
  margin: auto;
  font-size: 2.1rem;
  line-height: 3rem;
  color: vars.$color-text;
  text-align: center;
  pointer-events: none;
  transform: translateY(0.2rem);
}

.TextField-Placeholder {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 4.6rem;
  display: block;
  width: calc(100% - 6.6rem);
  height: 1em;
  margin: auto;
  font-size: 1.8rem;
  line-height: 1;
  color: #c4c4c4;
  pointer-events: none;
  transition: font-size 0.15s ease-out, transform 0.15s ease-out;
  transform-origin: left;
}

.TextField-input {
  position: relative;
  width: 100%;
  padding: 0;
  font-family: inherit;
  font-size: 1.8rem;
  font-weight: vars.$font-weight-medium;
  color: vars.$color-text-changed;
  background: transparent;
  border: none;
  outline: none;
  transition: transform 0.15s ease-out;
  appearance: none;

  &::placeholder {
    color: transparent;
  }

  &:focus,
  &.has-value {
    transform: translateY(1.1rem);

    & ~ .TextField-beforeIcon {
      transform: translateY(1.1rem);
    }

    & ~ .TextField-Placeholder {
      color: #b9b9b9;
      transform: translate(-3.1rem, -1.8rem) scale(0.778);
    }
  }
}

.TextField-body {
  position: relative;
  display: block;
  width: 100%;
  padding: 1.9rem 6.1rem 1.9rem 4.6rem;
  background: vars.$color-bg-changed-0;
  border-radius: 4px;

  &.is-invalid {
    background: #ff8e8e;

    .TextField-Placeholder,
    .TextField-input,
    .TextField-beforeIcon {
      color: vars.$color-text;
    }
  }

  &.is-disabled {
    background: transparent;
    border: 1px solid #4e4e4e;

    .TextField-Placeholder,
    .TextField-input {
      color: #b9b9b9;
    }

    .TextField-beforeIcon {
      color: vars.$color-text;
    }
  }
}

.TextField-notice {
  padding: 0 0.5rem;
  margin-top: 8px;
  font-size: 1.4rem;

  &.t-caution {
    font-weight: bold;
    color: #ff0000;
  }
}
</style>
